import type { CapacityPerformance } from "../../Reports/typings/reports.types";
import {
  isManagementPeriodDateBookable,
  type ManagementBookingWeekendSettings
} from "./management-period-weekend.utils";

export type ManagementCapaWeekdayPoint = {
  weekday: string;
  rate: number;
  display: number;
  displayCompare?: number;
};

export type ManagementCapaHourPoint = {
  hourLabel: string;
  rate: number;
  display: number;
  displayCompare?: number;
};

export type ManagementCapaDayPoint = {
  dayLabel: string;
  rate: number;
  display: number;
  displayCompare?: number;
};

const WEEKDAY_ORDER = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday"
] as const;

function toPercentDisplay(rate: number): number {
  return Math.round(rate * 100);
}

function isWeekdayKeyBookable(
  weekday: (typeof WEEKDAY_ORDER)[number],
  settings: ManagementBookingWeekendSettings
): boolean {
  if (weekday === "saturday") {
    return Boolean(settings.bookingsOnSaturdaysAllowed);
  }
  if (weekday === "sunday") {
    return Boolean(settings.bookingsOnSundaysAllowed);
  }
  return true;
}

/**
 * Resolves a capacity weekday key (`monday`) to a translated full label (`Monday` / `Montag`).
 */
export function formatManagementWeekdayLabel(
  weekday: string,
  translate: (key: string) => string
): string {
  return translate(weekday);
}

/**
 * API inventory names stay English (`Mobile Working`); map that sentinel for UI locale.
 */
export function formatManagementLocationDisplayName(
  name: string | undefined,
  translate: (key: string) => string
): string {
  if (!name) {
    return "";
  }
  if (name === "Mobile Working") {
    return translate("Mobile Working");
  }
  return name;
}

/**
 * Maps capacity-report weekday averages for Management bar charts.
 * Omits Sat/Sun when company booking settings disallow them.
 */
export function mapCapaWeekdayChartData(
  capacity: CapacityPerformance | undefined,
  weekendSettings: ManagementBookingWeekendSettings = {}
): ManagementCapaWeekdayPoint[] {
  if (!capacity?.averageCapacityPerformancePerWeekday) {
    return [];
  }
  const source = capacity.averageCapacityPerformancePerWeekday;
  return WEEKDAY_ORDER.filter(weekday => isWeekdayKeyBookable(weekday, weekendSettings)).map(
    weekday => {
      const rate = source[weekday] ?? 0;
      return {
        weekday,
        rate,
        display: toPercentDisplay(rate)
      };
    }
  );
}

/**
 * Maps capacity-report hourly series for Management bar charts.
 * Buckets into 2-hour units (average rate) so bars stay readable.
 */
export function mapCapaHourChartData(
  capacity: CapacityPerformance | undefined
): ManagementCapaHourPoint[] {
  const rows = capacity?.capacityPerformancePerHour ?? [];
  if (rows.length === 0) {
    return [];
  }

  const buckets = new Map<number, number[]>();
  for (const row of rows) {
    const bucketStart = Math.floor(row.hour / 2) * 2;
    const rates = buckets.get(bucketStart);
    if (rates) {
      rates.push(row.capacityRate);
    } else {
      buckets.set(bucketStart, [row.capacityRate]);
    }
  }

  return [...buckets.entries()]
    .sort(([a], [b]) => a - b)
    .map(([bucketStart, rates]) => {
      const rate = rates.reduce((sum, value) => sum + value, 0) / rates.length;
      return {
        hourLabel: `${bucketStart}:00`,
        rate,
        display: toPercentDisplay(rate)
      };
    });
}

/**
 * Maps capacity-report daily series for Management line charts.
 * Omits weekend days when company booking settings disallow them.
 */
export function mapCapaDayChartData(
  capacity: CapacityPerformance | undefined,
  weekendSettings: ManagementBookingWeekendSettings = {}
): ManagementCapaDayPoint[] {
  const rows = capacity?.capacityPerformancePerDay ?? [];
  return rows
    .filter(row => isManagementPeriodDateBookable(row.day, weekendSettings))
    .map(row => ({
      dayLabel: row.day,
      rate: row.capacityRate,
      display: toPercentDisplay(row.capacityRate)
    }));
}

/**
 * Merges primary + optional compare weekday series onto shared weekday keys.
 */
export function mergeCapaWeekdayChartData(
  primary: ManagementCapaWeekdayPoint[],
  compare?: ManagementCapaWeekdayPoint[]
): ManagementCapaWeekdayPoint[] {
  if (!compare?.length) {
    return primary;
  }
  const compareByWeekday = new Map(compare.map(point => [point.weekday, point.display]));
  return primary.map(point => ({
    ...point,
    displayCompare: compareByWeekday.get(point.weekday) ?? 0
  }));
}

/**
 * Merges primary + optional compare hour series onto shared hour labels.
 */
export function mergeCapaHourChartData(
  primary: ManagementCapaHourPoint[],
  compare?: ManagementCapaHourPoint[]
): ManagementCapaHourPoint[] {
  if (!compare?.length) {
    return primary;
  }
  const compareByHour = new Map(compare.map(point => [point.hourLabel, point.display]));
  const labels = new Set([
    ...primary.map(point => point.hourLabel),
    ...compare.map(p => p.hourLabel)
  ]);
  const primaryByHour = new Map(primary.map(point => [point.hourLabel, point]));
  return [...labels].map(hourLabel => {
    const primaryPoint = primaryByHour.get(hourLabel);
    return {
      hourLabel,
      rate: primaryPoint?.rate ?? 0,
      display: primaryPoint?.display ?? 0,
      displayCompare: compareByHour.get(hourLabel) ?? 0
    };
  });
}

/**
 * Merges primary + optional compare day series onto shared day labels.
 */
export function mergeCapaDayChartData(
  primary: ManagementCapaDayPoint[],
  compare?: ManagementCapaDayPoint[]
): ManagementCapaDayPoint[] {
  if (!compare?.length) {
    return primary;
  }
  const compareByDay = new Map(compare.map(point => [point.dayLabel, point.display]));
  const labels = new Set([
    ...primary.map(point => point.dayLabel),
    ...compare.map(p => p.dayLabel)
  ]);
  const primaryByDay = new Map(primary.map(point => [point.dayLabel, point]));
  return [...labels]
    .sort((a, b) => a.localeCompare(b))
    .map(dayLabel => {
      const primaryPoint = primaryByDay.get(dayLabel);
      return {
        dayLabel,
        rate: primaryPoint?.rate ?? 0,
        display: primaryPoint?.display ?? 0,
        displayCompare: compareByDay.get(dayLabel) ?? 0
      };
    });
}
